'use client';

import { Text } from '@/hen-ui/atomics/Text';
import { Icon, type IconName } from '@/hen-ui/atomics/Icon';
import { ToggleGroup, ToggleGroupItem } from '@/hen-ui/composites/ToggleGroup';
import { Flex } from '@/hen-ui/composites/Flex';

import type { CareerExplorerSelectorOption } from './types';

export interface CareerExplorerChipGroupProps {
  icon: IconName;
  label: string;
  optionalLabel?: string;
  options: CareerExplorerSelectorOption[];
  value: string | null;
  onValueChange: (value: string) => void;
  ariaLabel: string;
}

const PILL_CLASSNAME =
  'text-sm font-bold hover:bg-white data-[state=off]:text-secondary-700 data-[state=on]:hover:bg-black';

export function CareerExplorerChipGroup({
  icon,
  label,
  optionalLabel = 'Optional',
  options,
  value,
  onValueChange,
  ariaLabel,
}: CareerExplorerChipGroupProps) {
  return (
    <Flex direction='column' gap='md'>
      <Flex direction='row' gap='xxs' className='items-baseline'>
        <Icon
          icon={icon}
          size='sm'
          strokeWidth={1.5}
          className='self-center text-secondary-700'
          aria-hidden='true'
        />
        <Text tag='span' variant='b3' weight='bold' color='secondary'>
          {label}
        </Text>
        <Text tag='span' variant='b4' color='secondary' className='italic'>
          {optionalLabel}
        </Text>
      </Flex>
      <ToggleGroup
        type='single'
        wrap
        value={value ?? ''}
        onValueChange={onValueChange}
        aria-label={ariaLabel}
        className='gap-md'
      >
        {options.map((option) => (
          <ToggleGroupItem
            key={option.id}
            value={option.id}
            variant='blackFill'
            size='lg'
            className={PILL_CLASSNAME}
          >
            {option.label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>
    </Flex>
  );
}
